'use client';
import React, { useRef, useState } from 'react';

export default function HeroSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [heroSrc, setHeroSrc] = useState('/assets/images/asadetailingheropage-1779780673674.jpeg');

  const scrollToReveal = () => {
    const el = document.getElementById('reveal-stages');
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full overflow-hidden" style={{ height: '80vh', minHeight: '560px' }}>
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroSrc}
          alt="Rezultat premium detailing auto profesional ASA Detailing Botoșani"
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={() => setHeroSrc('/assets/images/no_image.png')}
        />
        {/* Cinematic overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(13,13,13,0.7) 0%, rgba(13,13,13,0.4) 50%, rgba(13,13,13,0.75) 100%)',
          }}
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-end h-full max-w-7xl mx-auto px-6 pb-10 md:pb-16" style={{ paddingTop: '80px' }}>
        <div className="max-w-3xl">
          <div className="metric-pill mb-3 hero-headline hidden sm:flex" style={{ animationDelay: '0.2s' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-ceramic inline-block" style={{ background: 'var(--ceramic-blue)' }} />
            Detailing Auto · Botoșani
          </div>

          <h1
            className="hero-headline font-manrope font-extrabold text-white leading-none tracking-tight mb-3"
            style={{ fontSize: 'clamp(26px, 4.5vw, 56px)', lineHeight: '0.96', letterSpacing: '-0.03em' }}
          >
            Detailing auto<br />
            <span style={{ color: 'var(--polished-aluminum)' }}>profesional,</span>{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #D4D4D8 50%, #3B82F6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              premium.
            </span>
          </h1>

          <p
            className="hero-sub font-dm text-sm md:text-base mb-4 max-w-xl leading-relaxed"
            style={{ color: 'rgba(212,212,216,0.75)', fontWeight: 300 }}
          >
            Corecție vopsea, acoperire ceramică și detailing interior — la sediul nostru din Botoșani.
          </p>

          {/* Address & Phone */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex items-center gap-2" style={{ color: 'rgba(212,212,216,0.85)' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'var(--ceramic-blue)', flexShrink: 0 }}>
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="9" r="2.5"/>
              </svg>
              <span style={{ fontSize: '14px', fontFamily: 'DM Sans, sans-serif' }}>E58 79 Mănăstirea Doamnei, 717114 Botoșani</span>
            </div>
            <div className="flex items-center gap-2" style={{ color: 'rgba(212,212,216,0.85)' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'var(--ceramic-blue)', flexShrink: 0 }}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <a href="tel:0787408552" style={{ fontSize: '14px', fontFamily: 'DM Sans, sans-serif', color: 'inherit', textDecoration: 'none' }}>0787 408 552</a>
            </div>
          </div>

          <div className="hero-ctas flex flex-wrap gap-3 mb-6">
            <a
              href="https://revmy.ro/service-provider-profile/botosani/detailing/asa-detailing"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Rezervă Acum
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="#compare" className="btn-secondary">
              Compară Pachetele
            </a>
          </div>

          {/* Hero stats */}
          <div className="hero-stats flex flex-wrap gap-6 md:gap-10">
            {[
              { value: '340+', label: 'Mașini Tratate' },
              { value: '4.98★', label: 'Rating Mediu' },
              { value: '6 ani', label: 'Garanție Ceramică' },
            ]?.map((stat) => (
              <div key={stat?.label}>
                <div
                  className="font-manrope font-extrabold text-white"
                  style={{ fontSize: '20px', letterSpacing: '-0.03em' }}
                >
                  {stat?.value}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--aluminum-dim)',
                    fontFamily: 'DM Sans, sans-serif',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    marginTop: '2px',
                  }}
                >
                  {stat?.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToReveal}
        className="absolute bottom-8 right-8 z-10 flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Derulează pentru a vedea etapele de detailing"
        suppressHydrationWarning
      >
        <span
          style={{
            fontSize: '10px',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--aluminum-dim)',
          }}
        >
          Derulează
        </span>
        <div
          className="w-px h-12"
          style={{ background: 'linear-gradient(to bottom, var(--aluminum-dim), transparent)' }}
        />
      </button>
    </section>
  );
}